import {
  Component,
  DoCheck,
  EventEmitter, Inject,
  Input,
  KeyValueDiffers,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { TableFilterService } from '@netfoundry-ui/feature/shared-services';
import _ from 'lodash';
import {firstValueFrom, Subscription} from 'rxjs';
import moment from 'moment';
import {
  EndpointServiceV2,
  PlatformServiceService,
  TEMPLATE_SEARCH_SERVICE,
  TemplateSearchService
} from "@netfoundry-ui/shared/apiv2";
import {NetworkV2} from "@netfoundry-ui/shared/model";
import {ApiService} from "@netfoundry-ui/shared/services";

export const KEY_CODES = {
  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40
};

@Component({
    selector: 'app-side-filter',
    templateUrl: './side-filter.component.html',
    styleUrls: ['./side-filter.component.scss'],
})
export class SideFilterComponent implements OnDestroy, DoCheck, OnInit {
    @Input() isTimeSearchAvailable = false;
    @Input() showCalendarFilter = false;
    @Input() appliedFilters: any[] = [];
    @Input() columnFilters: any = { name: '' };
    @Input() dateFilter: any = '24h';
    @Input() isUTC = false;
    @Input() isNotDialLogs = true;
    @Input() hideTextSearch = false;
    @Input() showAddFilter = false;
    newStartTime;
    newEndTime;
    rangeDates: Date[];
    externalFilters = [];
    addedFilters = [];
    filters = [];
    filteredServices = [];
    filteredIdentities = [];
    selectedServiceId;
    selectedIdentityId;
    minDate = moment().subtract(7, 'days').toDate();
    maxDate = moment().add(1, 'hours').toDate();
    dateFilterLabel = 'Past Day';
    menuLeft;
    menuTop;
    selectedRange;
    @ViewChild('calendar', { static: false }) calendar: any;
    @Output() filterRemoved = new EventEmitter();

    public filterString = '';
    timeSearch = '7d';
    isSelectingTime = false;
    private subscription: Subscription = new Subscription();
    private filterDiffer;

    showAdditionalFilters = false;
    servicesLoading = false;
    identitiesLoading = false;
    currentNetwork;
    serviceSearchText = '';
    identitySearchText = '';

    serviceFilterChangedDebounced: any = _.debounce(this.serviceFilterChanged.bind(this), 300, {trailing: true});
    identityFilterChangedDebounced: any = _.debounce(this.identityFilterChanged.bind(this), 300, {trailing: true});

    @ViewChild('filterMenu') filterMenu;
    @ViewChild('filterButton') filterButton;

    constructor(
      public filterService: TableFilterService,
      private differ: KeyValueDiffers,
      private serviceSvc: PlatformServiceService,
      private identitySvc: EndpointServiceV2,
      public apiService: ApiService,
      @Inject(TEMPLATE_SEARCH_SERVICE) public templateService: TemplateSearchService,
    ) {
        this.filterService.resetEvent.subscribe(() => {
            this.filterString = '';
            this.timeSearch = '7d';
        });
        this.filterDiffer = differ.find({}).create();
    }

    get filtersAdded() {
      return this.addedFilters.length > 0
    }

    ngOnInit() {
        this.subscription.add(this.filterService.externalFiltersChanged.subscribe((filters) => {
            this.externalFilters = filters || [];
            this.updateAppliedFilters();
        }));
        this.subscription.add(this.filterService.refreshFilters.subscribe((filters) => {
          this.externalFilters = this.filterService.externalFilters;
          this.updateAppliedFilters();
        }));
        this.subscription.add(this.apiService.currentNetwork.subscribe((network) => {
          this.currentNetwork = network as unknown as NetworkV2;
          this.serviceFilterChanged();
          this.identityFilterChanged();
        }));
        if (this.showAddFilter) {
          this.setDateRangeFilter('day');
        } else {
          const yesterday = moment().subtract(1, 'day').toDate();
          const now = moment().toDate();
          this.rangeDates = [yesterday, now];
          this.close();
        }
    }

    removeFilter(filter) {
        this.filterRemoved.emit(filter);
        this.filterService.removeFilter(filter);
        if (filter.columnId === 'name') {
            this.filterString = '';
        } else if (filter.columnId === 'serviceId') {
          this.selectedServiceId = undefined;
          this.addedFilters = this.addedFilters.filter((filter) => {
            return filter.columnId !== 'serviceId';
          });
        } else if (filter.columnId === 'identityId') {
          this.selectedIdentityId = undefined;
          this.addedFilters = this.addedFilters.filter((filter) => {
            return filter.columnId !== 'identityId';
          });
        }
        this.updateAppliedFilters();
    }

    ngDoCheck() {
        const changes = this.filterDiffer.diff(this.columnFilters);
        if (changes) {
            changes.forEachChangedItem((change) => {
                if (change.key === 'name') {
                    this.filterString = change.currentValue;
                }
            });
        }
    }

    close() {
        if (!_.isEmpty(this.rangeDates)) {
            this.filterService.setDateRange(moment.utc(this.rangeDates[0]), moment.utc(this.rangeDates[1]));
        } else {
            this.filterService.setDateFilter('24h');
        }
    }

    showFilters(event) {
      _.delay(() => {
        this.showAdditionalFilters = true;
        const container = this.filterButton.nativeElement?.getBoundingClientRect();
        const x = container.left + window.scrollX;
        const y = container.top + window.scrollY;
        this.menuLeft = x;
        this.menuTop = y + 35;
      }, 10);
    }

    closeFilters() {
      this.showAdditionalFilters = false;
    }

    serviceFilterChanged(event: any = {target: {value: ''}}) {
      if (event?.keyCode === KEY_CODES.LEFT_ARROW || event?.keyCode === KEY_CODES.RIGHT_ARROW || event?.keyCode === KEY_CODES.UP_ARROW || event?.keyCode === KEY_CODES.DOWN_ARROW) {
        return;
      }
      if (!event?.target?.value) {
        this.serviceSearchText = '';
      }
      const filterString = '%' + (event.target?.value || '') + '%';
      this.servicesLoading = true;
      this.templateService.getServices(filterString, this.currentNetwork?.id, 30).then((result: any) => {
        this.filteredServices = result;
      }).finally(() => {
        this.servicesLoading = false;
      });
    }

    identityFilterChanged(event: any = {target: {value: ''}}) {
      if (event?.keyCode === KEY_CODES.LEFT_ARROW || event?.keyCode === KEY_CODES.RIGHT_ARROW || event?.keyCode === KEY_CODES.UP_ARROW || event?.keyCode === KEY_CODES.DOWN_ARROW) {
        return;
      }
      if (!event?.target?.value) {
        this.identitySearchText = '';
      }
      const filterString = '%' + (event.target?.value || '') + '%';
      this.servicesLoading = true;
      this.templateService.getIdentities(filterString, this.currentNetwork?.id, 30).then((result: any) => {
        this.filteredIdentities = result;
      }).finally(() => {
        this.identitiesLoading = false;
      });
    }

    clearServiceFilter() {
      this.externalFilters = this.externalFilters.filter((filter) => {
        return filter.name !== 'service';
      });
      this.filterService.setExternalFilters(this.externalFilters);
    }

    serviceChanged(event) {
      const extFilters = this.filterService.externalFilters.filter((filter) => {
        return filter.columnId !== 'serviceId';
      });
      const addedFilters = this.addedFilters.filter((filter) => {
        return filter.columnId !== 'serviceId';
      });
      const selectedService = this.filteredServices.find((service) => {
        return service.id === this.selectedServiceId;
      });
      if (!_.isEmpty(selectedService)) {
        const filter = {columnId: 'serviceId', filterName: 'Service', label: selectedService.name, value: selectedService.id};
        extFilters.push(filter);
        addedFilters.push(filter);
      }
      this.filterService.setExternalFilters(extFilters);
      this.addedFilters = addedFilters;
    }

    identityChanged(event) {
      const extFilters = this.filterService.externalFilters.filter((filter) => {
        return filter.columnId !== 'identityId';
      });
      const addedFilters = this.addedFilters.filter((filter) => {
        return filter.columnId !== 'serviceId';
      });
      const selectedIdentity = this.filteredIdentities.find((identity) => {
        return identity.id === this.selectedIdentityId;
      });
      if (!_.isEmpty(selectedIdentity)) {
        const filter = {columnId: 'identityId', filterName: 'Identity', label: selectedIdentity.name, value: selectedIdentity.id};
        extFilters.push(filter);
        addedFilters.push(filter);
      }
      this.filterService.setExternalFilters(extFilters);
      this.addedFilters = addedFilters;
    }

    showTimeSelect() {
        this.isSelectingTime = true;
    }

    closeTime() {
        this.isSelectingTime = false;
    }

    pickDateFilter(time) {
        this.timeSearch = time;
        this.dateFilter = time;
        this.filterService.setDateFilter(time);
        this.rangeDates = null;
        this.closeTime();
    }

    next() {
        if (!this.filterService.isLastPage()) {
            this.filterService.nextPage();
        }
    }

    prev() {
        if (!this.filterService.isFirstPage()) {
            this.filterService.prevPage();
        }
    }

    setFilter() {
        _.debounce(() => {
            if (this.appliedFilters) {
                this.updateAppliedFilters();
            }
            this.filterService.setFilterString(this.filterString);
        }, 300)();
    }

    setDateRangeFilter(range) {
      let startDate;
      let label;
      let endDate = moment();
      let closeCalendar = true;
      this.selectedRange = range;
      switch (range) {
        case 'hour':
          startDate = moment().subtract(1, 'hours');
          label = 'Last Hour';
          break;
        case 'day':
          startDate = moment().subtract(24, 'hours');
          label = 'Last Day';
          break;
        case 'week':
          startDate = moment().subtract(7, 'days');
          label = 'Last Week';
          break;
        case 'month':
          startDate = moment().subtract(1, 'month');
          label = 'Last Month';
          break;
        case 'custom':
          if (!this.rangeDates) {
            return;
          }
          startDate = moment(this.rangeDates[0]);
          if (this.rangeDates[1] !== undefined && this.rangeDates[1] !== null) {
            endDate = moment(this.rangeDates[1]);
          }
          label = 'Custom';
          closeCalendar = false;
          break;
        default:
          startDate = moment().subtract(24, 'hours');
          label = 'Last Day';
          break;
      }
      const startDateRange = encodeURIComponent('>=' + startDate.toISOString());
      const endDateRange = encodeURIComponent('<=' + endDate.toISOString());
      this.columnFilters.startedAt = [startDateRange, endDateRange];
      const extFilters = this.filterService.externalFilters.filter((filter) => {
        return filter.columnId !== 'startedAt';
      });
      extFilters.push({ columnId: 'startedAt', label: label, filterName: 'Date Range' });
      this.filterService.setExternalFilters(extFilters);
      if (range !== 'custom') {
        this.rangeDates = [startDate.toDate(), endDate.toDate()];
      }
      this.close();
      if (closeCalendar) {
        this.calendar?.toggle();
      }
    }

    handleTimeChange(e) {
        this.isUTC = e.checked;
        this.filterService.setUTC.emit(this.isUTC);
    }

    updateAppliedFilters() {
        this.columnFilters.name = this.filterString;
        const filterExists = _.some(this.appliedFilters, { columnId: 'name' });
        if (filterExists) {
            if (_.isEmpty(this.columnFilters.name)) {
              // eslint-disable-next-line
                _.remove(this.appliedFilters as any, { columnId: 'name' });
            } else {
                _.forEach(this.appliedFilters, (filter) => {
                    if (filter.columnId === 'name') {
                        filter.label = this.columnFilters.name;
                    }
                });
            }
        } else if (!_.isEmpty(this.columnFilters.name)) {
            this.appliedFilters.push({
                columnId: 'name',
                label: this.columnFilters.name,
                filterName: 'Name',
            });
        }
        this.filters = [...this.appliedFilters, ...this.externalFilters];
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
